import { useCallback, useEffect, useMemo, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { Help, ModalShell, btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import type { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService, isApprovalRequired } from "../../features/gamification/service";
import type { ExpirySource, GamificationConfig, GamificationConfigPayload, NudgePreview } from "../../features/gamification/types";
import { LoadError } from "./GamificationPanels";
import { MAX_BUDGET, MAX_CREDITS, Toggle, numStr, parseOptionalWhole, parseWhole } from "./gamificationAdminUtils";

// ── Settings: switch + limits ────────────────────────────────────────────────
const EXPIRY_SOURCES: { key: ExpirySource; label: string }[] = [
  { key: "quest", label: "Quest credits" },
  { key: "campaign", label: "Festive campaign credits" },
  { key: "coupon", label: "Flat coupon credits" },
  { key: "referral", label: "Referral credits" },
];
const MAX_EXPIRY_DAYS = 3650;

/** Every setting as last saved, with the given switch value (a save sends the whole document). */
function payloadFrom(cfg: GamificationConfig, over: Partial<GamificationConfigPayload>): GamificationConfigPayload {
  const { enabled_at: _enabledAt, ...rest } = cfg;
  return { ...rest, ...over };
}

type SwitchTarget = { which: "rewards" | "wallet" | "lots"; to: boolean };

const SWITCH_COPY: Record<SwitchTarget["which"], { name: string; on: string; off: string }> = {
  rewards: {
    name: "rewards",
    on: "Users will start earning credits from every quest that is switched on. Credits already paid cannot be taken back by this switch.",
    off: "No new quest credits will be paid while rewards are off. Credits already paid stay in users' wallets. Credit expiry and take-back also stop.",
  },
  wallet: {
    name: "credit expiry and take-back",
    on: "Free credits past their expiry date will expire (overdue ones at the next run) and take-backs work again.",
    off: "Free credits stop expiring and take-backs are refused. Nothing is lost: credits keep being recorded and anything overdue expires once this is turned back on.",
  },
  lots: {
    name: "credit records",
    on: "Every new credit gets its own record again (source, expiry) and spends made while it was off are applied to the records at the next run. Credits paid while it was off never expire.",
    off: "Emergency stop for the credit records behind expiry and take-back. Payments, top-ups, rewards and spends keep working on the wallet balance as normal; only the extra records stop. Credits paid while it is off will never expire. Takes up to a minute to reach every server.",
  },
};

export default function GamificationSettingsPanel({ toast }: { toast: ReturnType<typeof useAdminToast> }) {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState<GamificationConfig | null>(null);
  const [loadError, setLoadError] = useState("");
  const [f, setF] = useState({
    cap: "", budget: "", expiry: "", includeExisting: false, takebackDays: "", floor: "", pushPerDay: "",
    frMax: "", frMin: "", frWindow: "", bhStart: "9", bhEnd: "19", bhDays: [0, 1, 2, 3, 4, 5] as number[], bySource: {} as Record<ExpirySource, string>,
    nudgeOn: true, nudgeDays: "", holdDays: "", autoTakeback: false, approvalsOn: false, apprBudget: "", apprTakeback: "", apprHours: "",
    apprCampUsers: "", apprCampCredits: "", docRetention: "",
  });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<SwitchTarget | null>(null);
  const [switching, setSwitching] = useState(false);
  const [preview, setPreview] = useState<NudgePreview | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const loadPreview = async () => {
    setPreviewBusy(true);
    try {
      setPreview(await gamificationService.getNudgePreview());
    } catch (e) {
      toast.showError(errMsg(e, "Failed to load the reminder preview"));
    } finally {
      setPreviewBusy(false);
    }
  };

  const apply = useCallback((c: GamificationConfig) => {
    setCfg(c);
    setF({
      cap: numStr(c.user_cap_30d), budget: numStr(c.monthly_budget), expiry: String(c.expiry_days),
      includeExisting: c.include_existing_users, takebackDays: String(c.takeback_days), floor: String(c.clawback_floor_credits),
      pushPerDay: String(c.push_per_day), frMax: String(c.fast_responder_max_minutes),
      frMin: String(c.fast_responder_min_enquiries), frWindow: String(c.fast_responder_window_days),
      bhStart: String(c.business_start_hour), bhEnd: String(c.business_end_hour), bhDays: [...c.business_days],
      bySource: Object.fromEntries(EXPIRY_SOURCES.map((s) => [s.key, numStr(c.expiry_days_by_source?.[s.key])])) as Record<ExpirySource, string>,
      nudgeOn: c.checklist_nudge_enabled, nudgeDays: String(c.checklist_nudge_days),
      holdDays: String(c.checklist_first_deal_hold_days), autoTakeback: c.auto_takeback_deleted_deal, approvalsOn: c.approvals_enabled,
      apprBudget: String(c.approval_quest_budget_threshold), apprTakeback: String(c.approval_takeback_threshold_credits),
      apprHours: String(c.approval_expiry_hours), apprCampUsers: String(c.approval_campaign_apply_users),
      apprCampCredits: String(c.approval_campaign_apply_credits), docRetention: String(c.intl_doc_retention_days),
    });
  }, []);

  const load = useCallback(async () => {
    try {
      apply(await gamificationService.getConfig());
      setLoadError("");
    } catch (e) {
      setCfg(null);
      setLoadError(errMsg(e, "Failed to load gamification settings"));
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { void load(); }, [load]);

  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));

  const parsed = useMemo(() => {
    const bySource: Partial<Record<ExpirySource, number>> = {};
    let bySourceBad = "";
    for (const s of EXPIRY_SOURCES) {
      const raw = f.bySource[s.key] ?? "";
      if (!raw.trim()) continue;
      const n = parseWhole(raw);
      if (n === null || n < 1 || n > MAX_EXPIRY_DAYS) bySourceBad = s.label;
      else bySource[s.key] = n;
    }
    return {
      cap: parseOptionalWhole(f.cap), budget: parseOptionalWhole(f.budget), expiry: parseWhole(f.expiry),
      takeback: parseWhole(f.takebackDays), floor: parseWhole(f.floor), push: parseWhole(f.pushPerDay),
      frMax: parseWhole(f.frMax), frMin: parseWhole(f.frMin), frWindow: parseWhole(f.frWindow), bhStart: parseWhole(f.bhStart), bhEnd: parseWhole(f.bhEnd), bySource, bySourceBad,
      nudgeDays: parseWhole(f.nudgeDays), holdDays: parseWhole(f.holdDays), apprBudget: parseWhole(f.apprBudget), apprTakeback: parseWhole(f.apprTakeback),
      apprHours: parseWhole(f.apprHours), apprCampUsers: parseWhole(f.apprCampUsers), apprCampCredits: parseWhole(f.apprCampCredits),
      docRetention: parseWhole(f.docRetention),
    };
  }, [f]);

  const inRange = (n: number | null, lo: number, hi: number) => n !== null && n >= lo && n <= hi;
  const validationError =
    parsed.cap === undefined ? "Per-user limit must be a whole number, or blank for no limit."
    : parsed.budget === undefined ? "Monthly budget must be a whole number, or blank for no budget."
    : !inRange(parsed.expiry, 1, MAX_EXPIRY_DAYS) ? `Expiry days must be a whole number from 1 to ${MAX_EXPIRY_DAYS}.`
    : parsed.bySourceBad ? `${parsed.bySourceBad}: expiry days must be a whole number from 1 to ${MAX_EXPIRY_DAYS}, or blank to use the default.`
    : !inRange(parsed.takeback, 1, 365) ? "Take-back window must be a whole number of days from 1 to 365."
    : !inRange(parsed.floor, 1, MAX_BUDGET) ? "Take-back floor must be a whole number of credits of at least 1."
    : !inRange(parsed.push, 0, 20) ? "Pushes per day must be a whole number from 0 to 20."
    : !inRange(parsed.frMax, 1, 1440) ? "Fast Responder reply time must be from 1 to 1440 minutes."
    : !inRange(parsed.frMin, 1, 10000) ? "Fast Responder minimum enquiries must be from 1 to 10000."
    : !inRange(parsed.frWindow, 1, 365) ? "Fast Responder window must be from 1 to 365 days."
    : !inRange(parsed.bhStart, 0, 23) ? "Business hours: the opening hour must be from 0 to 23."
    : !inRange(parsed.bhEnd, 1, 24) ? "Business hours: the closing hour must be from 1 to 24."
    : (parsed.bhStart ?? 0) >= (parsed.bhEnd ?? 0) ? "Business hours: closing must be after opening."
    : f.bhDays.length === 0 ? "Business hours: pick at least one working day."
    : !inRange(parsed.nudgeDays, 1, 365) ? "Checklist reminder gap must be from 1 to 365 days."
    : !inRange(parsed.holdDays, 0, 90) ? "First-deal hold must be a whole number of days from 0 to 90."
    : !inRange(parsed.apprBudget, 0, MAX_BUDGET) ? "Approval threshold for a quest budget must be a whole number of credits."
    : !inRange(parsed.apprTakeback, 0, MAX_BUDGET) ? "Approval threshold for a take-back must be a whole number of credits."
    : !inRange(parsed.apprHours, 1, 720) ? "Approval requests must expire after 1 to 720 hours."
    : !inRange(parsed.apprCampUsers, 1, 10_000_000) ? "Campaign apply approval: users must be a whole number of at least 1."
    : !inRange(parsed.apprCampCredits, 1, MAX_CREDITS) ? `Campaign apply approval: credits must be a whole number from 1 to ${MAX_CREDITS}.`
    : !inRange(parsed.docRetention, 1, MAX_EXPIRY_DAYS) ? `Verification documents must be kept 1 to ${MAX_EXPIRY_DAYS} days.`
    : "";

  const sameSources = (a: Partial<Record<ExpirySource, number>>, b: Partial<Record<ExpirySource, number>>) =>
    EXPIRY_SOURCES.every((s) => (a[s.key] ?? null) === (b[s.key] ?? null));

  const changes: string[] = [];
  if (cfg && !validationError) {
    if (parsed.cap !== cfg.user_cap_30d) {
      changes.push(parsed.cap === null ? "remove the per-user limit" : `set the per-user limit in any 30-day period to ${parsed.cap} credits`);
    }
    if (parsed.budget !== cfg.monthly_budget) {
      changes.push(parsed.budget === null ? "remove the monthly budget" : `set the monthly budget to ${parsed.budget} credits`);
    }
    if (parsed.expiry !== cfg.expiry_days) changes.push(`make free credits expire after ${parsed.expiry} days unless a source below says otherwise`);
    if (!sameSources(parsed.bySource, cfg.expiry_days_by_source ?? {})) changes.push("change the expiry days of individual credit sources (applies to credits paid from now on)");
    if (f.includeExisting !== cfg.include_existing_users) {
      changes.push(f.includeExisting ? "also pay users who finished onboarding before rewards were first turned on" : "stop paying users who finished onboarding before rewards were first turned on");
    }
    if (parsed.takeback !== cfg.takeback_days) changes.push(`allow take-backs for ${parsed.takeback} days after a reward`);
    if (parsed.floor !== cfg.clawback_floor_credits) changes.push(`let a take-back push a balance down to minus ${parsed.floor} credits`);
    if (parsed.push !== cfg.push_per_day) changes.push(`allow ${parsed.push} gamification pushes per user per day`);
    if (parsed.frMax !== cfg.fast_responder_max_minutes || parsed.frMin !== cfg.fast_responder_min_enquiries || parsed.frWindow !== cfg.fast_responder_window_days) {
      changes.push(`give Fast Responder to sellers whose median first reply is within ${parsed.frMax} business minutes over at least ${parsed.frMin} enquiries in ${parsed.frWindow} days`);
    }
    if (parsed.bhStart !== cfg.business_start_hour || parsed.bhEnd !== cfg.business_end_hour || [...f.bhDays].sort().join() !== [...cfg.business_days].sort().join()) {
      changes.push(`count Fast Responder reply times only between ${parsed.bhStart}:00 and ${parsed.bhEnd}:00 India time on ${f.bhDays.length} working day(s) (replies already recorded keep their time)`);
    }
    if (f.nudgeOn !== cfg.checklist_nudge_enabled) changes.push(f.nudgeOn ? "turn the checklist reminder on" : "turn the checklist reminder off");
    if (parsed.nudgeDays !== cfg.checklist_nudge_days) changes.push(`remind a user about the checklist at most once every ${parsed.nudgeDays} days`);
    if (parsed.holdDays !== cfg.checklist_first_deal_hold_days) {
      changes.push(parsed.holdDays === 0 ? "pay the first-deal reward at once, with no hold and no quality checks" : `hold the first-deal reward for ${parsed.holdDays} days and pay it only if the deal stays live and passes the quality checks`);
    }
    if (f.autoTakeback !== cfg.auto_takeback_deleted_deal) changes.push(f.autoTakeback ? "automatically take back the first-deal reward when that deal is deleted" : "stop automatically taking back the first-deal reward when that deal is deleted");
    if (f.approvalsOn !== cfg.approvals_enabled) changes.push(f.approvalsOn ? "require a second admin to approve risky changes" : "stop requiring a second admin for risky changes");
    if (parsed.apprBudget !== cfg.approval_quest_budget_threshold || parsed.apprTakeback !== cfg.approval_takeback_threshold_credits || parsed.apprHours !== cfg.approval_expiry_hours) {
      changes.push("change the approval thresholds and expiry");
    }
    if (parsed.apprCampUsers !== cfg.approval_campaign_apply_users || parsed.apprCampCredits !== cfg.approval_campaign_apply_credits) {
      changes.push(`ask a second admin before a campaign is applied to ${parsed.apprCampUsers} or more existing users or ${parsed.apprCampCredits} or more credits (raising these needs approval)`);
    }
    if (parsed.docRetention !== cfg.intl_doc_retention_days) changes.push(`delete verification documents ${parsed.docRetention} days after the decision`);
  }

  const saveLimits = async () => {
    if (!cfg || validationError) return;
    setSaving(true);
    try {
      const res = await gamificationService.updateConfig(payloadFrom(cfg, {
        user_cap_30d: parsed.cap ?? null, monthly_budget: parsed.budget ?? null, expiry_days: parsed.expiry ?? 0,
        include_existing_users: f.includeExisting, takeback_days: parsed.takeback ?? 0, clawback_floor_credits: parsed.floor ?? 0,
        push_per_day: parsed.push ?? 0, fast_responder_max_minutes: parsed.frMax ?? 0,
        fast_responder_min_enquiries: parsed.frMin ?? 0, fast_responder_window_days: parsed.frWindow ?? 0,
        business_start_hour: parsed.bhStart ?? 9, business_end_hour: parsed.bhEnd ?? 19, business_days: [...f.bhDays].sort(),
        expiry_days_by_source: parsed.bySource,
        checklist_nudge_enabled: f.nudgeOn, checklist_nudge_days: parsed.nudgeDays ?? 0,
        checklist_first_deal_hold_days: parsed.holdDays ?? 0, auto_takeback_deleted_deal: f.autoTakeback, approvals_enabled: f.approvalsOn,
        approval_quest_budget_threshold: parsed.apprBudget ?? 0, approval_takeback_threshold_credits: parsed.apprTakeback ?? 0,
        approval_expiry_hours: parsed.apprHours ?? 0, approval_campaign_apply_users: parsed.apprCampUsers ?? 0,
        approval_campaign_apply_credits: parsed.apprCampCredits ?? 0, intl_doc_retention_days: parsed.docRetention ?? 0,
      }));
      if (isApprovalRequired(res)) {
        toast.showSuccess("Sent for approval. A different admin has to approve it in the Approvals tab; nothing changed yet.");
        return;
      }
      apply(res);
      toast.showSuccess("Gamification settings saved");
    } catch (e) {
      toast.showError(errMsg(e, "Failed to save gamification settings"));
    } finally {
      setSaving(false);
    }
  };

  const confirmSwitch = async () => {
    if (!cfg || confirm === null) return;
    // Only the switch changes here; every other setting is sent exactly as last saved.
    const body = payloadFrom(cfg, confirm.which === "rewards" ? { enabled: confirm.to }
      : confirm.which === "wallet" ? { wallet_expiry_enabled: confirm.to } : { wallet_lots_enabled: confirm.to });
    setSwitching(true);
    try {
      const saved = await gamificationService.updateConfig(body);
      if (isApprovalRequired(saved)) {
        setConfirm(null);
        toast.showSuccess("Sent for approval. A different admin has to approve it in the Approvals tab; the switch has not changed yet.");
        return;
      }
      setCfg(saved);
      setConfirm(null);
      const now = confirm.which === "rewards" ? saved.enabled : confirm.which === "wallet" ? saved.wallet_expiry_enabled : saved.wallet_lots_enabled;
      const name = SWITCH_COPY[confirm.which].name;
      toast.showSuccess(`${name.charAt(0).toUpperCase()}${name.slice(1)} are now ${now ? "ON" : "OFF"}`);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to change the switch"));
    } finally {
      setSwitching(false);
    }
  };

  const num = (id: string, value: string, onChange: (v: string) => void, min: number, placeholder?: string) => (
    <input id={id} type="number" min={min} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  );

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Rewards switch and limits</h2>
      <p className="text-gray-500 text-xs mb-4">
        Turns the whole quest rewards system on or off and sets how much it can pay out.
        <span className="block text-gray-600">Example: turn it off to pause every quest reward at once without touching the individual quests.</span>
      </p>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !cfg ? (
        <LoadError message={`${loadError}. Editing is disabled so the current settings are not overwritten.`} onRetry={() => { setLoading(true); void load(); }} />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-4 bg-gray-800/50 border border-gray-800 rounded-xl p-4">
            <Toggle checked={cfg.enabled} onChange={(v) => setConfirm({ which: "rewards", to: v })} label="Rewards on or off" />
            <div>
              <p className={`text-xl font-bold ${cfg.enabled ? "text-emerald-400" : "text-gray-300"}`}>Rewards are {cfg.enabled ? "ON" : "OFF"}</p>
              <p className="text-gray-500 text-xs">Rewards ship OFF. Nothing is paid until you turn them on here.</p>
              {cfg.enabled_at && <p className="text-gray-500 text-xs mt-0.5">Rewards were first turned on at {formatISTDateTime(cfg.enabled_at)}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 bg-gray-800/50 border border-gray-800 rounded-xl p-4">
            <Toggle checked={cfg.wallet_expiry_enabled} onChange={(v) => setConfirm({ which: "wallet", to: v })} label="Credit expiry and take-back on or off" />
            <div>
              <p className={`text-sm font-semibold ${cfg.wallet_expiry_enabled ? "text-emerald-400" : "text-amber-400"}`}>Credit expiry and take-back are {cfg.wallet_expiry_enabled ? "ON" : "OFF"}</p>
              <p className="text-gray-500 text-xs">Stop switch for expiring free credits and taking rewards back. Nothing is lost while it is off: credits keep being recorded and overdue ones expire once it is back on. It also needs "Rewards" to be ON.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 bg-gray-800/50 border border-gray-800 rounded-xl p-4">
            <Toggle checked={cfg.wallet_lots_enabled} onChange={(v) => setConfirm({ which: "lots", to: v })} label="Credit records on or off" />
            <div>
              <p className={`text-sm font-semibold ${cfg.wallet_lots_enabled ? "text-emerald-400" : "text-amber-400"}`}>Credit records are {cfg.wallet_lots_enabled ? "ON" : "OFF"}</p>
              <p className="text-gray-500 text-xs">Emergency stop for the per-credit records (source and expiry date) kept beside every wallet. Leave ON. Turning it OFF never blocks a payment or a spend; credits paid while it is OFF simply never expire.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls} htmlFor="gs-cap">Per-user limit in any 30-day period</label>
              {num("gs-cap", f.cap, (v) => set({ cap: v }), 0, "No limit")}
              <Help>Most credits one user can earn from quests in any rolling 30-day period. Blank = no limit.</Help>
            </div>
            <div>
              <label className={labelCls} htmlFor="gs-budget">Monthly budget (credits)</label>
              {num("gs-budget", f.budget, (v) => set({ budget: v }), 0, "No budget")}
              <Help>Total quest credits all users can earn in a month. Blank = no budget.</Help>
            </div>
            <div>
              <label className={labelCls} htmlFor="gs-expiry">Expiry days (default)</label>
              {num("gs-expiry", f.expiry, (v) => set({ expiry: v }), 1)}
              <Help>Days until free credits expire when no source below sets its own. Welcome credits and top-up bonus credits never expire. Applies to credits paid from now on.</Help>
            </div>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold">Expiry days per source</h3>
            <p className="text-gray-500 text-xs mb-2">Leave blank to use the default above. Example: quest credits 30, flat coupon credits blank (uses the default).</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {EXPIRY_SOURCES.map((s) => (
                <div key={s.key}>
                  <label className={labelCls} htmlFor={`gs-src-${s.key}`}>{s.label}</label>
                  {num(`gs-src-${s.key}`, f.bySource[s.key] ?? "", (v) => set({ bySource: { ...f.bySource, [s.key]: v } }), 1, `Default (${f.expiry || "—"})`)}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls} htmlFor="gs-takeback">Take-back window (days)</label>
              {num("gs-takeback", f.takebackDays, (v) => set({ takebackDays: v }), 1)}
              <Help>How long after a reward it can be taken back. 1 to 365.</Help>
            </div>
            <div>
              <label className={labelCls} htmlFor="gs-floor">Take-back floor (credits below zero)</label>
              {num("gs-floor", f.floor, (v) => set({ floor: v }), 1)}
              <Help>A take-back may leave a balance down to minus this many credits. Below that nothing is taken and the credit is flagged for review. At least 1.</Help>
            </div>
            <div>
              <label className={labelCls} htmlFor="gs-push">Gamification pushes per user per day</label>
              {num("gs-push", f.pushPerDay, (v) => set({ pushPerDay: v }), 0)}
              <Help>Cap on reward and expiry push notifications per user per day. 0 to 20; 0 sends none. The checklist reminder is not held back by this cap.</Help>
            </div>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold">Fast Responder badge</h3>
            <p className="text-gray-500 text-xs mb-2">A seller earns it when the median time to first reply to a buyer's enquiry (counted only in the business hours you set below, India time) is short enough over enough enquiries.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-frmax">Median first reply within (minutes)</label>
                {num("gs-frmax", f.frMax, (v) => set({ frMax: v }), 1)}
                <Help>1 to 1440.</Help>
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-frmin">Minimum answered enquiries</label>
                {num("gs-frmin", f.frMin, (v) => set({ frMin: v }), 1)}
                <Help>1 to 10000.</Help>
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-frwin">Look back (days)</label>
                {num("gs-frwin", f.frWindow, (v) => set({ frWindow: v }), 1)}
                <Help>1 to 365.</Help>
              </div>
            </div>
            <h4 className="text-gray-300 text-xs font-semibold mt-4">Business hours (India time)</h4>
            <p className="text-gray-500 text-xs mb-2">Time outside these hours does not count against a seller. Example: 9 to 19 on Mon to Sat; a buyer writes at 18:30 and the seller replies at 9:15 next morning: 45 minutes. Changing this affects replies recorded from now on.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-bh-start">Opens at (hour, 0 to 23)</label>
                {num("gs-bh-start", f.bhStart, (v) => set({ bhStart: v }), 0)}
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-bh-end">Closes at (hour, 1 to 24)</label>
                {num("gs-bh-end", f.bhEnd, (v) => set({ bhEnd: v }), 1)}
              </div>
              <div>
                <span className={labelCls}>Working days</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                    <label key={d} className="flex items-center gap-1 text-xs text-gray-300">
                      <input
                        type="checkbox"
                        checked={f.bhDays.includes(i)}
                        onChange={(e) => set({ bhDays: e.target.checked ? [...f.bhDays, i] : f.bhDays.filter((x) => x !== i) })}
                      />
                      {d}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold">Checklist reminder</h3>
            <p className="text-gray-500 text-xs mb-2">A daily job sends one push to users who have not finished the onboarding checklist and could still earn credits.</p>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={f.nudgeOn} onChange={(e) => set({ nudgeOn: e.target.checked })} />
                Send the reminder
              </label>
              <div>
                <label className={labelCls} htmlFor="gs-nudge">At most once every (days)</label>
                {num("gs-nudge", f.nudgeDays, (v) => set({ nudgeDays: v }), 1)}
                <Help>1 to 365. A user who muted rewards updates gets nothing. The "pushes per user per day" cap does not apply to this reminder.</Help>
              </div>
              <button type="button" className={btnGhost} onClick={() => void loadPreview()} disabled={previewBusy}>
                {previewBusy ? "Loading…" : "Preview the reminder"}
              </button>
            </div>
            {preview && (
              <div className="mt-3 max-w-xl rounded-xl border border-gray-700 bg-gray-800/50 p-4 text-sm">
                <p className="text-gray-500 text-[11px] mb-2">How a user sees it (the amount is an example)</p>
                <div className="rounded-lg bg-gray-900 border border-gray-700 p-3">
                  <p className="text-white font-semibold">{preview.title}</p>
                  <p className="text-gray-300 text-xs mt-0.5">{preview.body}</p>
                </div>
                <ul className="mt-3 space-y-1 text-xs text-gray-400">
                  <li>Sent: {preview.runs_at}, to a user at most once every {preview.every_days} days.</li>
                  <li>Where: {[preview.bell && "the bell icon", preview.push && "a phone push"].filter(Boolean).join(" and ") || "nowhere (both channels are off for this message)"}.</li>
                  <li>Who: users who finished onboarding, still have credits to earn and have not finished the checklist, sellers and buyers alike. Muted users are skipped.</li>
                  <li>
                    {preview.rewards_enabled
                      ? `Right now: ${preview.would_receive} of the first ${preview.checked_users} onboarded users checked would get it.`
                      : "Rewards are OFF, so nothing is sent until they are turned on."}
                  </li>
                  {!preview.enabled && preview.rewards_enabled && <li className="text-amber-400">The reminder is switched off above, so nobody gets it.</li>}
                </ul>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold">First-deal reward hold</h3>
            <p className="text-gray-500 text-xs mb-2">The first-deal reward waits until the deal has been live for this many days and still passes the quality checks.</p>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-hold">Hold (days)</label>
                {num("gs-hold", f.holdDays, (v) => set({ holdDays: v }), 0)}
                <Help>0 to 90. 0 means no hold and no quality checks: the reward is paid at once.</Help>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={f.autoTakeback} onChange={(e) => set({ autoTakeback: e.target.checked })} />
                Automatically take back the reward if the first deal is deleted
              </label>
            </div>
            <Help>Auto take-back follows the take-back window above and the normal take-back rules. Off: an admin takes it back by hand.</Help>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold">Two-admin approval</h3>
            <p className="text-gray-500 text-xs mb-2">When ON, risky changes are not applied at once: a different admin must approve them in the Approvals tab. Leave OFF if you are the only admin, or you would lock yourself out.</p>
            <label className="flex items-center gap-2 text-sm text-gray-300 mb-3">
              <input type="checkbox" checked={f.approvalsOn} onChange={(e) => set({ approvalsOn: e.target.checked })} />
              Require a second admin for risky changes
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-ap-budget">Quest budget above (credits)</label>
                {num("gs-ap-budget", f.apprBudget, (v) => set({ apprBudget: v }), 0)}
                <Help>Raising a quest budget above this, or removing it, needs approval.</Help>
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-ap-takeback">Take-back above (credits)</label>
                {num("gs-ap-takeback", f.apprTakeback, (v) => set({ apprTakeback: v }), 0)}
                <Help>A single take-back larger than this needs approval.</Help>
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-ap-hours">Request expires after (hours)</label>
                {num("gs-ap-hours", f.apprHours, (v) => set({ apprHours: v }), 1)}
                <Help>1 to 720. Turning rewards on, or paying existing users, always needs approval while this is ON.</Help>
              </div>
            </div>
            <h4 className="text-gray-300 text-xs font-semibold mt-4">Festive campaign apply to existing users (always on)</h4>
            <p className="text-gray-500 text-xs mb-2">Even with the box above unticked, applying a campaign to existing users waits for a different admin when it would pay at least this many users OR this many credits in total. Smaller applies run at once. Raising either figure also needs a second admin.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-ap-camp-users">Users at or above</label>
                {num("gs-ap-camp-users", f.apprCampUsers, (v) => set({ apprCampUsers: v }), 1)}
                <Help>Default 100.</Help>
              </div>
              <div>
                <label className={labelCls} htmlFor="gs-ap-camp-credits">Total credits at or above</label>
                {num("gs-ap-camp-credits", f.apprCampCredits, (v) => set({ apprCampCredits: v }), 1)}
                <Help>Default 10000.</Help>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-gray-200 text-sm font-semibold">Verification documents (sellers outside India)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls} htmlFor="gs-doc-retention">Delete documents after (days)</label>
                {num("gs-doc-retention", f.docRetention, (v) => set({ docRetention: v }), 1)}
                <Help>Days after an admin approves or rejects. The uploaded file and its details are deleted; only who decided what and when is kept. Default 30.</Help>
              </div>
            </div>
          </div>

          <div>
            <label className="flex items-start gap-2 text-sm text-gray-300">
              <input type="checkbox" className="mt-1" checked={f.includeExisting} onChange={(e) => set({ includeExisting: e.target.checked })} />
              Also pay users who finished onboarding before rewards were first turned on
            </label>
            <p className="text-amber-400 text-xs mt-1">This can pay out to every existing user at once, which can cost far more than rewarding only new users. Check the monthly budget before enabling it.</p>
          </div>

          {validationError && <p className="text-red-400 text-xs" role="alert">{validationError}</p>}
          {changes.length > 0 && (
            <p className="text-sm text-indigo-300 bg-indigo-950/40 border border-indigo-900 rounded-lg px-3 py-2">
              Saving will {changes.join("; ")}.
            </p>
          )}
          <button onClick={saveLimits} disabled={saving || !!validationError || changes.length === 0} className={btnPrimary}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}

      {confirm !== null && (
        <ModalShell
          title={`Turn ${SWITCH_COPY[confirm.which].name} ${confirm.to ? "ON" : "OFF"}?`}
          onClose={() => !switching && setConfirm(null)}
        >
          <p className="text-sm text-gray-300 mb-2">
            {confirm.to ? SWITCH_COPY[confirm.which].on : SWITCH_COPY[confirm.which].off}
          </p>
          {confirm.which === "rewards" && confirm.to && cfg && cfg.include_existing_users && (
            <p className="text-amber-400 text-xs mb-2">"Also pay users who finished onboarding before rewards were first turned on" is ticked, so existing users can earn too.</p>
          )}
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setConfirm(null)} disabled={switching} className={btnGhost}>Cancel</button>
            <button onClick={confirmSwitch} disabled={switching} className={btnPrimary}>
              {switching ? "Saving…" : confirm.to ? "Turn ON" : "Turn OFF"}
            </button>
          </div>
        </ModalShell>
      )}
    </section>
  );
}
